import {
  Pencil,
  Trash2,
  Loader2,
  CheckCircle,
  XCircle,
  Settings,
} from 'lucide-react';

import { AddTaxonomyForm } from './AddTaxonomyForm';
import { EditTaxonomyForm } from './EditTaxonomyForm';
import { RemoveTaxonomyProperty } from './RemoveTaxonomyProperty';

import { Button } from '@/components/ui/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from '@/components/ui/Table';
import { TaxonomyGetResponse } from '@/services/TaxonomiesService';

interface DialogStateType {
  type: 'add' | 'edit' | 'removeTaxonomyProperty' | null;
  item: TaxonomyGetResponse | null;
}

const repeatableGroups = [
  'sales_support',
  'prints_and_colors',
  'pairs_with',
  'seo_content',
];

export function TaxonomyTable({
  customerId,
  isLoading,
  displayedItems,
  setDialogState,
  dialogState,
}: {
  customerId: number | null;
  isLoading: boolean;
  displayedItems: TaxonomyGetResponse[];
  setDialogState: React.Dispatch<React.SetStateAction<DialogStateType>>;
  dialogState: DialogStateType;
}) {
  function closeDialog() {
    setDialogState({ type: null, item: null });
  }

  const StatusIcon = ({ status }: { status: boolean }) => {
    return status ? (
      <CheckCircle className="h-5 w-5 text-green-600" />
    ) : (
      <XCircle className="h-5 w-5 text-red-600" />
    );
  };

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Category</TableHead>
            <TableHead>Group</TableHead>
            <TableHead>Group Name</TableHead>
            <TableHead>Property</TableHead>
            <TableHead>Description</TableHead>
            <TableHead>Examples</TableHead>
            <TableHead>Filter Values</TableHead>
            <TableHead>Order</TableHead>
            <TableHead>Repeatable</TableHead>
            <TableHead className="text-center">Actions</TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={8} className="py-20 text-center">
                <div className="flex w-full items-center justify-center">
                  <div className="flex flex-col items-center justify-center gap-4 p-4">
                    <Loader2 className="h-12 w-12 animate-spin text-gray-700" />
                    <p className="text-lg font-medium text-gray-800">
                      Loading informations...
                    </p>
                  </div>
                </div>
              </TableCell>
            </TableRow>
          ) : displayedItems.length === 0 && !isLoading ? (
            <TableRow>
              <TableCell colSpan={8} className="py-20 text-center">
                <div className="flex w-full items-center justify-center">
                  <div className="flex flex-col items-center justify-center gap-4 p-4">
                    <p className="text-md text-gray-500">
                      No taxonomies data available.
                    </p>
                  </div>
                </div>
              </TableCell>
            </TableRow>
          ) : (
            displayedItems.map((item) => (
              <TableRow
                key={`${item.property}-${item.groupName}-${item.categoryName}-${item.description}`}
              >
                <TableCell>{item.categoryName}</TableCell>
                <TableCell>{item.type}</TableCell>
                <TableCell>{item.groupName}</TableCell>
                <TableCell className="max-w-[200px] truncate">
                  {item.property}
                </TableCell>
                <TableCell className="max-w-[200px] truncate">
                  {item.description}
                </TableCell>
                <TableCell className="max-w-[200px] truncate">
                  {item.examples}
                </TableCell>
                <TableCell className="max-w-[200px] truncate">
                  {Array.isArray(item.filterValues)
                    ? item.filterValues.join(',')
                    : item.filterValues}
                </TableCell>
                <TableCell>{item.order}</TableCell>
                <TableCell>
                  <div className="flex justify-center rounded-sm p-1">
                    <StatusIcon status={repeatableGroups.includes(item.type)} />
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end space-x-2">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setDialogState({ type: 'edit', item })}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>

                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() =>
                        setDialogState({
                          type: 'removeTaxonomyProperty',
                          item,
                        })
                      }
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild disabled={!!customerId}>
                        <Button variant="outline" size="icon" disabled={true}>
                          <Settings className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuSeparator />
                        <DropdownMenuLabel>Actions</DropdownMenuLabel>
                        <DropdownMenuItem>
                          Add for all customers
                        </DropdownMenuItem>
                        <DropdownMenuItem>
                          Delete for all customers
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {dialogState.type === 'add' && (
        <AddTaxonomyForm open={true} onClose={closeDialog} />
      )}

      {dialogState.type === 'edit' && dialogState.item && (
        <EditTaxonomyForm
          editingItem={dialogState.item}
          isOpen={true}
          onClose={closeDialog}
        />
      )}

      {dialogState.type === 'removeTaxonomyProperty' && dialogState.item && (
        <RemoveTaxonomyProperty
          itemSelected={dialogState.item}
          isOpen={true}
          onClose={closeDialog}
        />
      )}
    </div>
  );
}
